import {
  Badge,
  Button,
  Chip,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@heroui/react';
import { useQueryClient } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import { AlertTriangle, CheckCircle2, Edit, Eye, Gauge, Globe, Group, Loader2, MapPin, MoreVertical, Percent, Plus, PowerOff, Trash2, TriangleAlert, User, UserPlus, X, Zap } from 'lucide-react';
import React, { useState } from 'react';
import ReactCountryFlag from 'react-country-flag';
import { useNavigate } from 'react-router-dom';

import { useHeroToast } from '../../../../hooks';
import {
  useAssignOrganizationToGroup,
  useCreatePoi,
  useDeleteOrganizationFromFirestore,
  useGetOrganizationGroups,
  useGetUser,
  usePatchOrganizationStatus,
} from '../../../../queries';
import type { ResponseDataProps } from '../../../../queries/organizationsQueryFirebase/useGetOrganizationCollectionEndpoint';
import { formatFirestoreDate } from '../../../../utils/formatFirestoreDate';
import { AssignToGroupModal } from '../../components/AssignToGroupModal';

type OpenManagerModal = (id: string, accountManager?: string) => void;

const ManagerCell: React.FC<{ org: ResponseDataProps; openManagerModal: OpenManagerModal }> = ({
  org,
  openManagerModal,
}) => {
  const { data: manager } = useGetUser(org.account_manager || '');

  if (!manager) {
    return (
      <button
        type="button"
        onClick={() => openManagerModal(org.id, org.account_manager)}
        className="inline-flex items-center cursor-pointer hover:opacity-70 transition-opacity"
        aria-label="Assign manager"
      >
        <UserPlus size={16} color="#888" />
      </button>
    );
  }

  return (
    <Popover placement="bottom">
      <PopoverTrigger>
        <button
          type="button"
          className="inline-flex items-center cursor-pointer hover:opacity-70 transition-opacity"
          aria-label="Show manager details"
        >
          <User size={16} />
        </button>
      </PopoverTrigger>
      <PopoverContent>
        <div className="px-3 py-2">
          <div className="text-sm font-semibold">
            {manager.name}
            {' '}
            {manager.surname}
          </div>
          <div className="text-xs text-gray-500">{manager.email}</div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

const ActionsCell: React.FC<{ org: ResponseDataProps }> = ({ org }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useHeroToast();
  const { mutate: deleteOrg, isPending } = useDeleteOrganizationFromFirestore();
  const { mutate: generatePoi, isPending: isGenerate } = useCreatePoi();
  const { mutate: toggleStatus, isPending: isTogglingStatus } = usePatchOrganizationStatus();
  const isOrgDisabled = org.isDisabled === true;
  const [assignGroupOpen, setAssignGroupOpen] = useState(false);
  const [deletePopoverOpen, setDeletePopoverOpen] = React.useState(false);
  const [poiModalOpen, setPoiModalOpen] = useState(false);
  const [poiStatus, setPoiStatus] = useState<'pending' | 'done' | 'error'>('pending');
  const [poiError, setPoiError] = useState<string | null>(null);
  const poiAutoCloseRef = React.useRef<NodeJS.Timeout | null>(null);

  React.useEffect(() => () => {
    if (poiAutoCloseRef.current) clearTimeout(poiAutoCloseRef.current);
  }, []);

  const handleDelete = (id: string) => {
    deleteOrg(
      { id },
      {
        onSuccess: () => {
          showSuccess('Successfully deleted organization');
          queryClient.refetchQueries({
            queryKey: ['organizations'],
            exact: true,
          });
          setDeletePopoverOpen(false);
        },
        onError: () => {
          showError('Error while deleting organization');
          setDeletePopoverOpen(false);
        },
      },
    );
  };

  const handleCreatePoi = (id: string) => {
    if (poiAutoCloseRef.current) {
      clearTimeout(poiAutoCloseRef.current);
      poiAutoCloseRef.current = null;
    }
    setPoiStatus('pending');
    setPoiError(null);
    setPoiModalOpen(true);

    generatePoi(
      { id },
      {
        onSuccess: () => {
          setPoiStatus('done');
          showSuccess('Successfully generated poi');
          queryClient.resetQueries({ queryKey: ['organizations'] });
          poiAutoCloseRef.current = setTimeout(() => {
            setPoiModalOpen(false);
          }, 1800);
        },
        onError: (err: any) => {
          const message = err?.response?.data?.message
            || err?.message
            || 'Error generated poi';
          setPoiStatus('error');
          setPoiError(message);
          showError(message);
        },
      },
    );
  };

  const handleToggleStatus = () => {
    const next = !isOrgDisabled;
    toggleStatus(
      { id: org.id, isDisabled: next },
      {
        onSuccess: () => {
          showSuccess(next ? 'Organizacja wyłączona' : 'Organizacja włączona');
          queryClient.invalidateQueries({ queryKey: ['organizations'] });
        },
        onError: () => {
          showError(next ? 'Nie udało się wyłączyć organizacji' : 'Nie udało się włączyć organizacji');
        },
      },
    );
  };

  const closePoiModal = () => {
    if (isGenerate) return;
    if (poiAutoCloseRef.current) {
      clearTimeout(poiAutoCloseRef.current);
      poiAutoCloseRef.current = null;
    }
    setPoiModalOpen(false);
  };

  const isBusy = isPending || isGenerate || isTogglingStatus;

  return (
    <div className="flex items-center justify-end h-[40px]">
      <Dropdown placement="bottom-end">
        <DropdownTrigger>
          <Button
            isIconOnly
            size="sm"
            variant="light"
            isDisabled={isBusy}
            aria-label="Open organization actions menu"
          >
            <MoreVertical size={16} />
          </Button>
        </DropdownTrigger>
        <DropdownMenu
          aria-label="Organization actions"
          onAction={(key) => {
            switch (key) {
              case 'view':
                navigate(`/organizations/${org.id}`);
                break;
              case 'edit':
                navigate(`/organizations/${org.id}/edit`);
                break;
              case 'stripe-fees':
                navigate(`/organizations/${org.id}/stripe-fees`);
                break;
              case 'ai-limits':
                navigate(`/organizations/${org.id}/ai-limits`);
                break;
              case 'generate-poi':
                handleCreatePoi(org.id);
                break;
              case 'assign-to-group':
                setAssignGroupOpen(true);
                break;
              case 'enable':
              case 'disable':
                handleToggleStatus();
                break;
              case 'delete':
                setDeletePopoverOpen(true);
                break;
              default:
                break;
            }
          }}
        >
          {isOrgDisabled ? [
            <DropdownItem
              key="enable"
              startContent={<Zap size={14} className="text-success" />}
              className="text-success"
              color="success"
            >
              Włącz organizację
            </DropdownItem>,
          ] : [
            <DropdownItem key="view" startContent={<Eye size={14} />}>
              View
            </DropdownItem>,
            <DropdownItem key="edit" startContent={<Edit size={14} />}>
              Edit
            </DropdownItem>,
            <DropdownItem key="stripe-fees" startContent={<Percent size={14} />}>
              Ustaw prowizję
            </DropdownItem>,
            <DropdownItem key="ai-limits" startContent={<Gauge size={14} />}>
              Limity AI
            </DropdownItem>,
            <DropdownItem key="generate-poi" startContent={<MapPin size={14} />}>
              Generate POI
            </DropdownItem>,
            <DropdownItem key="assign-to-group" startContent={<Group size={14} />}>
              Przypisz do grupy
            </DropdownItem>,
            <DropdownItem
              key="disable"
              startContent={<PowerOff size={14} className="text-warning" />}
              className="text-warning"
              color="warning"
            >
              Wyłącz organizację
            </DropdownItem>,
            <DropdownItem
              key="delete"
              color="danger"
              className="text-danger"
              startContent={<Trash2 size={14} />}
            >
              Delete
            </DropdownItem>,
          ]}
        </DropdownMenu>
      </Dropdown>

      <AssignToGroupModal
        isOpen={assignGroupOpen}
        organizationId={org.id}
        organizationName={org.hotel || org.name}
        currentGroupId={org.groupId ?? null}
        onClose={() => {
          setAssignGroupOpen(false);
          queryClient.invalidateQueries({ queryKey: ['organizations'] });
        }}
      />

      <Modal
        isOpen={poiModalOpen}
        onOpenChange={(open) => { if (!open) closePoiModal(); }}
        size="sm"
        isDismissable={!isGenerate}
        hideCloseButton={isGenerate}
      >
        <ModalContent>
          {() => (
            <>
              <ModalHeader className="flex flex-col gap-1">
                <div className="flex items-center gap-2">
                  <MapPin size={18} className="text-primary" />
                  <span>Generowanie POI</span>
                </div>
                <p className="text-xs font-normal text-gray-500">
                  Hotel:
                  {' '}
                  <span className="font-medium text-gray-700">{org.hotel || org.name}</span>
                </p>
              </ModalHeader>
              <ModalBody>
                <div className="flex flex-col items-center gap-3 py-3 text-center">
                  {poiStatus === 'pending' && (
                    <>
                      <Loader2 size={36} className="text-primary animate-spin" />
                      <div>
                        <div className="text-sm font-semibold text-gray-900">Pobieranie POI…</div>
                        <div className="text-xs text-gray-500 mt-1">
                          Pobieram listę punktów zainteresowania w okolicy hotelu.
                          Może to potrwać do minuty.
                        </div>
                      </div>
                    </>
                  )}
                  {poiStatus === 'done' && (
                    <>
                      <CheckCircle2 size={36} className="text-success" />
                      <div>
                        <div className="text-sm font-semibold text-gray-900">Done</div>
                        <div className="text-xs text-gray-500 mt-1">
                          POI zostały wygenerowane.
                        </div>
                      </div>
                    </>
                  )}
                  {poiStatus === 'error' && (
                    <>
                      <AlertTriangle size={36} className="text-danger" />
                      <div>
                        <div className="text-sm font-semibold text-danger">Błąd</div>
                        <div className="text-xs text-gray-600 mt-1 max-w-[380px]">
                          {poiError || 'Wystąpił nieznany błąd.'}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </ModalBody>
              <ModalFooter>
                {poiStatus === 'pending' && (
                  <Button variant="light" isDisabled>
                    Trwa generowanie…
                  </Button>
                )}
                {poiStatus === 'done' && (
                  <Button color="success" variant="flat" onPress={closePoiModal}>
                    Zamknij
                  </Button>
                )}
                {poiStatus === 'error' && (
                  <>
                    <Button variant="light" onPress={closePoiModal}>
                      Zamknij
                    </Button>
                    <Button color="primary" onPress={() => handleCreatePoi(org.id)}>
                      Spróbuj ponownie
                    </Button>
                  </>
                )}
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>

      <Modal
        isOpen={deletePopoverOpen}
        onOpenChange={setDeletePopoverOpen}
        size="sm"
        isDismissable={!isPending}
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1">Delete organization?</ModalHeader>
              <ModalBody>
                <p className="text-sm text-gray-600">
                  This action cannot be undone. Are you sure you want to delete
                  {' '}
                  <span className="font-semibold">
                    "
                    {org.hotel || org.name}
                    "
                  </span>
                  ?
                </p>
              </ModalBody>
              <ModalFooter>
                <Button variant="light" onPress={onClose} isDisabled={isPending}>
                  Cancel
                </Button>
                <Button color="danger" onPress={() => handleDelete(org.id)} isLoading={isPending}>
                  Delete
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </div>
  );
};

const GroupCell: React.FC<{ org: ResponseDataProps }> = ({ org }) => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useHeroToast();
  const { data: groups } = useGetOrganizationGroups();
  const { mutate: assign, isPending } = useAssignOrganizationToGroup();

  const group = (groups ?? []).find((g) => g.id === org.groupId);
  if (!org.groupId || !group) {
    return <span className="text-xs text-gray-400">—</span>;
  }

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    assign(
      { organizationId: org.id, groupId: null, previousGroupId: org.groupId ?? null },
      {
        onSuccess: () => {
          showSuccess('Usunięto z grupy');
          queryClient.invalidateQueries({ queryKey: ['organizations'] });
        },
        onError: () => showError('Nie udało się usunąć z grupy'),
      },
    );
  };

  return (
    <Chip
      size="sm"
      variant="flat"
      color="primary"
      startContent={<Group size={12} />}
      endContent={(
        <button
          type="button"
          onClick={handleRemove}
          disabled={isPending}
          className="ml-1 hover:opacity-70 disabled:opacity-30"
          aria-label="Usuń z grupy"
          title="Usuń z grupy"
        >
          <X size={11} />
        </button>
      )}
    >
      {group.name || '(bez nazwy)'}
    </Chip>
  );
};

export const OrganizationColumns = (
  openManagerModal: OpenManagerModal,
  // Admin z zawężeniem `onlyAttraction` ogląda organizacje, ale ich nie zmienia —
  // ukrywamy całą kolumnę akcji (trzy kropki), a nie tylko pojedyncze pozycje.
  hideActions = false,
): ColumnDef<ResponseDataProps>[] => {
  const columns: ColumnDef<ResponseDataProps>[] = [
    {
      accessorKey: 'hotel',
      header: 'Hotel',
      cell: ({ row }) => {
        const org = row.original;
        const label = org.hotel || org.name || '—';
        return (
          <div className="flex items-center gap-2">
            <span className={org.isDisabled ? 'text-gray-400 line-through' : ''}>
              {label}
            </span>
            {org.isDisabled && (
            <Chip
              color="warning"
              variant="flat"
              size="sm"
              startContent={<PowerOff size={12} />}
            >
              Disabled
            </Chip>
            )}
          </div>
        );
      },
    },
    {
      id: 'group',
      header: 'Group',
      enableSorting: false,
      cell: ({ row }) => <GroupCell org={row.original} />,
    },
    {
      accessorKey: 'languages',
      header: 'Languages',
      cell: ({ row }) => {
        const languages = row.original.languages ?? [];
        if (!languages.length) {
          return <TriangleAlert size={14} color="red" />;
        }
        return (
          <Popover placement="bottom">
            <PopoverTrigger>
              <button
                type="button"
                className="flex items-center gap-2 cursor-pointer hover:opacity-70 transition-opacity"
              >
                <Globe size={16} />
                <span className="text-sm">{languages.length}</span>
              </button>
            </PopoverTrigger>
            <PopoverContent>
              <div className="px-2 py-2">
                <div className="flex items-center gap-2 flex-wrap">
                  {languages.map((language) => (
                    <ReactCountryFlag
                      key={language}
                      countryCode={language === 'en' ? 'gb' : language}
                      svg
                      style={{ width: 24, height: 16 }}
                    />
                  ))}
                </div>
              </div>
            </PopoverContent>
          </Popover>
        );
      },
      enableSorting: false,
    },
    {
      accessorKey: 'associated_attractions',
      header: 'Attractions',
      cell: ({ row }) => {
        const navigate = useNavigate();
        const attractions = row.original.associated_attractions;

        // Function to get attraction count
        const getAttractionCount = () => {
          if (!attractions) return 0;

          if (Array.isArray(attractions)) {
            return attractions.length;
          }

          if (typeof attractions === 'string') {
            if (attractions.includes(',')) {
              return attractions.split(',').filter((a) => a.trim()).length;
            }

            try {
              const parsed = JSON.parse(attractions);
              if (Array.isArray(parsed)) {
                return parsed.length;
              }
            } catch {
            // Not a JSON array
            }

            if (attractions.trim().length > 0) {
              return 1;
            }
          }

          if (typeof attractions === 'number') {
            return attractions;
          }

          return 0;
        };

        const count = getAttractionCount();

        return (
          <div className="flex items-center gap-2">
            <span className={count > 0 ? 'text-green-600 font-semibold' : 'text-gray-400'}>
              {count}
            </span>
            <Button
              isIconOnly
              size="sm"
              variant="light"
              onPress={() => navigate(`/organizations/${row.original.id}/assign-attractions`)}
              className="hover:bg-primary/10"
            >
              <Plus size={14} className="text-primary" />
            </Button>
          </div>
        );
      },
    },
    {
      accessorKey: 'plan',
      header: 'Plan',
      cell: ({ row }) => {
        const plan = row.original.plan || 'BASIC';
        const colorMap: Record<string, 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'danger'> = {
          BASIC: 'default',
          STANDARD: 'primary',
          PRO: 'secondary',
          ULTIMATE: 'success',
          CUSTOM: 'warning',
          TEST: 'danger',
        };
        return (
          <Badge color={colorMap[plan] || 'default'} variant="flat">
            {plan}
          </Badge>
        );
      },
    },
    {
      accessorKey: 'createdAt',
      header: 'Created At',
      cell: ({ row }) => {
        const date = row.original.createdAt;
        // Use the helper function to format the date
        // If date is null/undefined, it will return '-'
        // If date is invalid, it will return 'Invalid Date'
        // Otherwise it will format properly handling Firestore Timestamps
        return formatFirestoreDate(date, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        }, 'en-GB', '-');
      },
    },
    {
      accessorKey: 'pois',
      header: 'Pois',
    },
    {
      id: 'manager',
      header: 'Manager',
      meta: { align: 'center' },
      cell: ({ row }) => (
        <ManagerCell
          org={row.original}
          openManagerModal={openManagerModal}
        />
      ),
    },
    {
      id: 'actions',
      header: '',
      enableSorting: false,
      cell: ({ row }) => <ActionsCell org={row.original} />,
    },
  ];

  return columns.filter((column) => !(hideActions && column.id === 'actions'));
};
